import styles from './accordion-header.scss?inline';
import { LitElement, unsafeCSS, CSSResult } from 'lit';
import { property, queryAssignedElements } from 'lit/decorators.js';
import { literal, html } from 'lit/static-html.js';
import { C11nPrefix, C11nPrefixLiteral } from 'virtual:c11n-prefix';
import { c11nElement } from '../core';
import { C11nIconLiteral } from '../icon';
import '../icon';

export type AccordionHeaderSize = 'medium' | 'large';
export type AccordionHeaderMediumWeight = 'normal' | 'bold';
export type AccordionHeaderLargeWeight = 'bold' | 'heavy';
export type AccordionHeaderIconPosition = 'leading' | 'trailing';

@c11nElement()
export class AccordionHeaderComponent extends LitElement {
  static selector = `${C11nPrefix}-accordion-header`;
  static literal = literal`${C11nPrefixLiteral}-accordion-header`;
  static override styles: CSSResult = unsafeCSS(styles);

  @property({ attribute: 'size', type: String, converter: (value: string | null) => value ?? 'medium' })
  _size?: AccordionHeaderSize = 'medium';

  @property({ attribute: 'open', type: Boolean, reflect: true })
  _open: boolean = false;

  @property({ attribute: 'font-weight', type: String })
  fontWeight?: AccordionHeaderMediumWeight | AccordionHeaderLargeWeight;

  @property({ attribute: 'icon-position', type: String, converter: (value: string | null) => value ?? 'trailing' })
  iconPosition?: AccordionHeaderIconPosition = 'trailing';

  @property({ attribute: 'c11n-web-accordion-header', type: Boolean, reflect: true })
  readonly _c11n_web_accordion_header: boolean = true;

  @queryAssignedElements({ slot: 'summary', selector: 'span' })
  summaryEls!: Array<HTMLSpanElement>;

  handleSummarySlotChange() {
    this.requestUpdate();
  }

  getSummary() {
    if (this.summaryEls.length) {
      return html`
      <span class="c11n-accordion__summary">
        <slot name="summary" @slotchange=${this.handleSummarySlotChange}></slot>
      </span>`;
    } else {
      return html`
      <slot name="summary" @slotchange=${this.handleSummarySlotChange}></slot>`;
    }
  }

  override render() {
    return html`
      <button title="${this._open ? 'show less' : 'show more'}" aria-expanded="${this._open}">
        <span class="c11n-accordion__header__container">
          <span class="c11n-accordion__header">
            <slot></slot>
          </span>
          ${this.getSummary()}
        </span>
        <span class="c11n-accordion__icon__container">
          <${C11nIconLiteral} class="c11n-accordion__icon" name="${this._size === 'medium' ? 'caret-down' : 'accordion-closed-caret-stroke'}"></${C11nIconLiteral}>
        </span>
      </button>
    `;
  }
}

export const C11nAccordionHeader = AccordionHeaderComponent.selector;
export const C11nAccordionHeaderLiteral = AccordionHeaderComponent.literal;
